import React, { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileSpreadsheet, Plus, RefreshCw, Download, Calendar, FileText, AlertTriangle, FileType, X, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { SubscriptionRequired } from "@/components/ui/subscription-required";
import { use1099Vendors, Vendor } from "@/hooks/use-vendors";
import { useContractors, Contractor } from "@/hooks/use-contractors";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function TaxFilingsPage(): JSX.Element {
  const { toast } = useToast();
  const { currentBusiness: selectedBusiness } = useCurrentBusiness();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedPeriod, setSelectedPeriod] = useState("all"); // all, q1, q2, q3, q4, custom
  const [showPrepareDialog, setShowPrepareDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null);
  const [recipientType, setRecipientType] = useState<"vendor" | "contractor">("vendor");
  const [form1099Type, setForm1099Type] = useState("MISC");
  const [boxAmounts, setBoxAmounts] = useState({
    nonemployeeCompensation: "",
    rents: "",
    royalties: "",
    otherIncome: "",
    federalTaxWithheld: ""
  });
  const [generatedForm, setGeneratedForm] = useState<string | null>(null);
  
  // Fetch vendors marked for 1099 tracking
  const { data: vendors1099 = [], isLoading: isLoadingVendors, refetch: refetchVendors } = use1099Vendors(
    selectedBusiness?.id || 0
  );
  
  // Fetch contractors for 1099 preparation
  const { data: contractors = [], isLoading: isLoadingContractors, refetch: refetchContractors } = useContractors(
    selectedBusiness?.id || 0
  );

  // Get payer information (business)
  const [businessInfo, setBusinessInfo] = useState<any>({ 
    name: selectedBusiness?.name || "Business", 
    address: {} 
  });
  
  // Fetch business info when business changes
  useEffect(() => {
    if (selectedBusiness?.id) {
      fetch(`/api/businesses/${selectedBusiness.id}`)
        .then(res => res.json())
        .then(data => {
          setBusinessInfo(data);
        })
        .catch(err => {
          console.error("Error fetching business info:", err);
          // Use default info on error
          setBusinessInfo({ 
            name: selectedBusiness?.name || "Business", 
            address: {} 
          });
        });
    }
  }, [selectedBusiness?.id, selectedBusiness?.name]);

  const generatePDF = async (recipient: any): Promise<string> => {
    try {
      // Create PDF document with fixed dimensions
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      
      // Background color for header
      doc.setFillColor(245, 245, 245);
      doc.rect(0, 0, 210, 30, 'F');
      
      // Add a title with better styling
      doc.setFontSize(20);
      doc.setTextColor(44, 62, 80);
      doc.text("1099 Form Preview", 105, 15, { align: "center" });
      
      // Add current date
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, 22, { align: "center" });
      
      // Add horizontal line
      doc.setLineWidth(0.5);
      doc.setDrawColor(200, 200, 200);
      doc.line(20, 32, 190, 32);
      
      // Add form information with better styling
      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);
      doc.text("Form 1099-" + form1099Type, 20, 40);
      doc.text(`Tax Year: ${selectedYear}`, 20, 48);
      
      // Add section divider
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.3);
      doc.line(20, 52, 190, 52);
      
      // Add payer information
      doc.setFillColor(240, 250, 255);
      doc.rect(20, 55, 170, 30, 'F');
      
      doc.setFontSize(14);
      doc.setTextColor(41, 128, 185);
      doc.text("PAYER (Your Business)", 25, 62);
      
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      const payer = businessInfo || { name: "Your Business Name", address: {} };
      doc.text(`${payer.name || "Business Name"}`, 25, 70);
      
      if (payer.address) {
        let addressText = "";
        if (payer.address.street) addressText += payer.address.street;
        if (payer.address.city || payer.address.state || payer.address.zipCode) {
          const cityStateZip = [
            payer.address.city, 
            payer.address.state, 
            payer.address.zipCode
          ].filter(Boolean).join(", ");
          if (addressText) addressText += "\n" + cityStateZip;
          else addressText = cityStateZip;
        }
        
        if (addressText) {
          doc.text(addressText, 25, 77);
        }
      }
      
      // Add recipient information
      doc.setFillColor(245, 245, 245);
      doc.rect(20, 90, 170, 36, 'F');
      
      doc.setFontSize(14);
      doc.setTextColor(41, 128, 185);
      doc.text(`RECIPIENT (${recipientType === "vendor" ? "Vendor" : "Contractor"})`, 25, 97);
      
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      doc.text(`${recipient.name}`, 25, 105);
      
      if (recipient.address) {
        let recipientAddressText = "";
        if (recipient.address.street) recipientAddressText += recipient.address.street;
        if (recipient.address.city || recipient.address.state || recipient.address.zipCode) {
          const cityStateZip = [
            recipient.address.city, 
            recipient.address.state, 
            recipient.address.zipCode
          ].filter(Boolean).join(", ");
          if (recipientAddressText) recipientAddressText += "\n" + cityStateZip;
          else recipientAddressText = cityStateZip;
        }
        
        if (recipientAddressText) {
          doc.text(recipientAddressText, 25, 112);
        }
      }
      
      doc.text(`Tax ID: ${recipient.taxId || "Not provided"}`, 25, 120);
      
      // Add payment information
      doc.setFillColor(230, 240, 250);
      doc.rect(20, 132, 170, 10, 'F');
      
      doc.setFontSize(14);
      doc.setTextColor(41, 128, 185);
      doc.text("PAYMENT INFORMATION", 25, 139);
      
      // Create payment information table
      const tableData = [];
      if (form1099Type === "MISC") {
        if (boxAmounts.rents) tableData.push(["Box 1: Rents", `$${boxAmounts.rents}`]);
        if (boxAmounts.royalties) tableData.push(["Box 2: Royalties", `$${boxAmounts.royalties}`]);
        if (boxAmounts.otherIncome) tableData.push(["Box 3: Other Income", `$${boxAmounts.otherIncome}`]);
      } else if (form1099Type === "NEC") {
        if (boxAmounts.nonemployeeCompensation) 
          tableData.push(["Box 1: Nonemployee Compensation", `$${boxAmounts.nonemployeeCompensation}`]);
      }
      
      if (boxAmounts.federalTaxWithheld) 
        tableData.push(["Federal Income Tax Withheld", `$${boxAmounts.federalTaxWithheld}`]);
      
      // Add empty row if no data
      if (tableData.length === 0) {
        tableData.push(["No payment information entered", "-"]);
      }
      
      // Add table with better styling
      autoTable(doc, {
        startY: 145,
        head: [["Description", "Amount"]],
        body: tableData,
        theme: "grid",
        headStyles: { 
          fillColor: [41, 128, 185],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        styles: { 
          fontSize: 11,
          cellPadding: 6
        },
        columnStyles: {
          0: { cellWidth: 120 },
          1: { cellWidth: 50, halign: "right" }
        }
      });
      
      // Add signature line
      const finalY = (doc as any).lastAutoTable.finalY + 20;
      doc.setDrawColor(100, 100, 100);
      doc.line(20, finalY, 100, finalY);
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text("Payer Signature", 20, finalY + 5);
      
      // Add disclaimer
      doc.setFillColor(245, 245, 245);
      doc.rect(0, 280, 210, 17, 'F');
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text("DISCLAIMER: This is a preview and not an official IRS form. Please consult with a tax professional before filing.", 105, 288, { align: "center" });
      
      // Convert to base64 string with proper encoding that works in browsers
      const pdfBase64 = doc.output('datauristring');
      return pdfBase64;
    } catch (error) {
      console.error("Error generating PDF:", error);
      // Return a placeholder in case of error to avoid breaking the form
      const errorDoc = new jsPDF();
      errorDoc.setFontSize(16);
      errorDoc.setTextColor(255, 0, 0);
      errorDoc.text("Error generating PDF preview", 105, 20, { align: "center" });
      errorDoc.setFontSize(10);
      errorDoc.text("Please try again or download the form", 105, 30, { align: "center" });
      return errorDoc.output("datauristring");
    }
  };

  const handleRefresh = () => {
    refetchVendors();
    refetchContractors();
    toast({
      title: "Data refreshed",
      description: "The vendor and contractor information has been refreshed.",
    });
  };

  const handlePrepare1099 = (recipient?: any, type: "vendor" | "contractor" = "vendor") => {
    if (recipient) {
      if (type === "vendor") {
        setSelectedVendor(recipient);
        setSelectedContractor(null);
      } else {
        setSelectedContractor(recipient);
        setSelectedVendor(null);
      }
      setRecipientType(type);
    } else {
      setSelectedVendor(null);
      setSelectedContractor(null);
    }
    
    // Reset form fields
    setForm1099Type("MISC");
    setBoxAmounts({
      nonemployeeCompensation: "",
      rents: "",
      royalties: "",
      otherIncome: "",
      federalTaxWithheld: ""
    });
    setGeneratedForm(null);
    setShowPrepareDialog(true);
  };

  const handleFormSubmit = async () => {
    try {
      // Get the selected recipient based on the type
      const recipient = recipientType === "vendor" ? selectedVendor : selectedContractor;
      
      if (!recipient) {
        toast({
          title: "Error",
          description: "No recipient selected for 1099 form.",
          variant: "destructive",
        });
        return;
      }

      // Create 1099 form data
      const formData = {
        recipientType,
        recipientId: recipient.id,
        businessId: selectedBusiness?.id,
        tax_year: selectedYear,
        form_type: form1099Type,
        amounts: boxAmounts,
        status: "draft"
      };

      // Generate PDF preview first (we'll skip the actual API call for now)
      const pdfData = await generatePDF(recipient);
      setGeneratedForm(pdfData);
      
      toast({
        title: "1099 form prepared",
        description: "The 1099 form has been prepared and is ready for review.",
      });
      
      // In a real implementation, we would submit the form to create a 1099 record
      // using a properly configured API endpoint
    } catch (error) {
      console.error("Error preparing 1099 form:", error);
      toast({
        title: "Error",
        description: "There was an error preparing the 1099 form. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleExportToExcel = () => {
    try {
      const workbook = XLSX.utils.book_new();
      
      // Create a worksheet for vendors
      const vendorsData = Array.isArray(vendors1099) ? vendors1099.map((v: any) => ({
        "Vendor Name": v.name,
        "Tax ID": v.taxId || "Not provided",
        "Form Type": v.form1099Type || "Not specified",
        "Status": v.status,
        "Email": v.email,
        "Phone": v.phone,
      })) : [];
      
      // Create a worksheet for contractors
      const contractorsData = Array.isArray(contractors) ? contractors.map((c: any) => ({
        "Contractor Name": c.name,
        "Tax ID": c.taxId || "Not provided",
        "Status": c.status,
        "Email": c.email,
        "Phone": c.phone,
      })) : [];
      
      // Add worksheets to the workbook
      const vendorsSheet = XLSX.utils.json_to_sheet(vendorsData);
      XLSX.utils.book_append_sheet(workbook, vendorsSheet, "1099 Vendors");
      
      const contractorsSheet = XLSX.utils.json_to_sheet(contractorsData);
      XLSX.utils.book_append_sheet(workbook, contractorsSheet, "Contractors");
      
      // Generate Excel file and download
      XLSX.writeFile(workbook, `1099_recipients_${selectedYear}.xlsx`);
      
      toast({
        title: "Export successful",
        description: "The 1099 recipients data has been exported to Excel.",
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast({
        title: "Export failed",
        description: "There was an error exporting the data to Excel.",
        variant: "destructive",
      });
    }
  };

  const handleExportToPDF = () => {
    try {
      // Create PDF document
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.text(`1099 Recipients Report - ${selectedYear}`, 105, 15, { align: "center" });
      
      // Add vendor table
      doc.setFontSize(14);
      doc.text("1099 Vendors", 20, 30);
      
      const vendorData = Array.isArray(vendors1099) ? vendors1099.map((v: any) => [
        v.name,
        v.taxId || "Not provided",
        v.form1099Type || "Not specified",
        v.status,
        v.email || "N/A"
      ]) : [];
      
      autoTable(doc, {
        startY: 35,
        head: [["Vendor Name", "Tax ID", "Form Type", "Status", "Email"]],
        body: vendorData,
        theme: "striped",
        headStyles: { 
          fillColor: [66, 139, 202],
          textColor: [255, 255, 255]
        }
      });
      
      // Add contractor table
      const finalY = (doc as any).lastAutoTable.finalY || 35;
      doc.setFontSize(14);
      doc.text("Contractors", 20, finalY + 15);
      
      const contractorData = Array.isArray(contractors) ? contractors.map((c: any) => [
        c.name,
        c.taxId || "Not provided",
        c.status,
        c.email || "N/A"
      ]) : [];
      
      autoTable(doc, {
        startY: finalY + 20,
        head: [["Contractor Name", "Tax ID", "Status", "Email"]],
        body: contractorData,
        theme: "striped",
        headStyles: { 
          fillColor: [66, 139, 202],
          textColor: [255, 255, 255]
        }
      });
      
      // Generate PDF file and download
      doc.save(`1099_recipients_${selectedYear}.pdf`);
      
      toast({
        title: "Export successful",
        description: "The 1099 recipients data has been exported to PDF.",
      });
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      toast({
        title: "Export failed",
        description: "There was an error exporting the data to PDF.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadForm = () => {
    if (!generatedForm) return;
    
    // Create a link and click it to download the PDF
    const link = document.createElement("a");
    link.href = generatedForm;
    const fileName = `1099-${form1099Type}_${
      recipientType === "vendor" 
        ? (selectedVendor?.name || "vendor") 
        : (selectedContractor?.name || "contractor")
    }_${selectedYear}.pdf`;
    link.download = fileName.replace(/\s+/g, "_");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Download started",
      description: "The 1099 form is being downloaded to your device.",
    });
  };

  if (!selectedBusiness) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <h1 className="text-3xl font-bold tracking-tight">Tax Filings</h1>
          <p className="text-muted-foreground mb-8">
            Please select a business to access tax filings.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Tax Filings</h1>
            <p className="text-muted-foreground">
              Manage your tax filings, including 1099 forms for vendors and contractors
            </p>
          </div>
          <div className="flex space-x-2">
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingVendors || isLoadingContractors ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        
        {/* Tax Filings Tabs */}
        <div className="mt-6">
          <Tabs defaultValue="1099" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="1099">1099 Forms</TabsTrigger>
              <TabsTrigger value="history">Filing History</TabsTrigger>
              <TabsTrigger value="settings">Filing Settings</TabsTrigger>
            </TabsList>
            
            <TabsContent value="1099">
              <Card>
                <CardHeader>
                  <CardTitle>1099 Form Preparation</CardTitle>
                  <CardDescription>
                    Manage and prepare 1099 forms for vendors and contractors
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex gap-4">
                      <div className="w-40">
                        <Select 
                          value={selectedYear}
                          onValueChange={(value) => setSelectedYear(value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Year" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 5 }, (_, i) => (
                              <SelectItem 
                                key={currentYear - i} 
                                value={(currentYear - i).toString()}
                              >
                                {currentYear - i}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-40">
                        <Select 
                          value={selectedPeriod}
                          onValueChange={(value) => setSelectedPeriod(value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Period" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Year</SelectItem>
                            <SelectItem value="q1">Q1 (Jan-Mar)</SelectItem>
                            <SelectItem value="q2">Q2 (Apr-Jun)</SelectItem>
                            <SelectItem value="q3">Q3 (Jul-Sep)</SelectItem>
                            <SelectItem value="q4">Q4 (Oct-Dec)</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            <FileSpreadsheet className="h-4 w-4 mr-2" />
                            Export
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={handleExportToExcel}>
                            Export to Excel
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleExportToPDF}>
                            Export to PDF
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  
                  {/* Recipient Type Tabs */}
                  <Tabs defaultValue="vendors" className="w-full">
                    <TabsList className="mb-4">
                      <TabsTrigger value="vendors">Vendors</TabsTrigger>
                      <TabsTrigger value="contractors">Contractors</TabsTrigger>
                    </TabsList>
                    
                    {/* Vendors Tab */}
                    <TabsContent value="vendors">
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Vendor Name</TableHead>
                              <TableHead>Tax ID</TableHead>
                              <TableHead>Form Type</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {isLoadingVendors ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center py-8">
                                  <RefreshCw className="h-5 w-5 animate-spin mx-auto" />
                                  <p className="mt-2 text-sm text-muted-foreground">Loading vendors...</p>
                                </TableCell>
                              </TableRow>
                            ) : vendors1099.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center py-8">
                                  <AlertTriangle className="h-8 w-8 mx-auto text-yellow-500" />
                                  <p className="mt-2 text-sm font-medium">No vendors marked for 1099 tracking</p>
                                  <p className="text-sm text-muted-foreground">
                                    Mark vendors for 1099 tracking in the Vendors page
                                  </p>
                                </TableCell>
                              </TableRow>
                            ) : (
                              vendors1099.map((vendor: Vendor) => (
                                <TableRow key={vendor.id}>
                                  <TableCell className="font-medium">{vendor.name}</TableCell>
                                  <TableCell>{vendor.taxId || "Not provided"}</TableCell>
                                  <TableCell>{vendor.form1099Type || "Not specified"}</TableCell>
                                  <TableCell>
                                    <Badge variant={vendor.status === "active" ? "default" : "secondary"}>
                                      {vendor.status}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button 
                                      onClick={() => handlePrepare1099(vendor, "vendor")}
                                      variant="ghost"
                                      size="sm"
                                    >
                                      <FileText className="h-4 w-4 mr-2" />
                                      Prepare 1099
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </TabsContent>
                    
                    {/* Contractors Tab */}
                    <TabsContent value="contractors">
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Contractor Name</TableHead>
                              <TableHead>Tax ID</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {isLoadingContractors ? (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center py-8">
                                  <RefreshCw className="h-5 w-5 animate-spin mx-auto" />
                                  <p className="mt-2 text-sm text-muted-foreground">Loading contractors...</p>
                                </TableCell>
                              </TableRow>
                            ) : contractors.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center py-8">
                                  <AlertTriangle className="h-8 w-8 mx-auto text-yellow-500" />
                                  <p className="mt-2 text-sm font-medium">No contractors found</p>
                                  <p className="text-sm text-muted-foreground">
                                    Add contractors in the Contractors page
                                  </p>
                                </TableCell>
                              </TableRow>
                            ) : (
                              contractors.map((contractor: Contractor) => (
                                <TableRow key={contractor.id}>
                                  <TableCell className="font-medium">{contractor.name}</TableCell>
                                  <TableCell>{contractor.taxId || "Not provided"}</TableCell>
                                  <TableCell>
                                    <Badge variant={contractor.status === "active" ? "default" : "secondary"}>
                                      {contractor.status}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button 
                                      onClick={() => handlePrepare1099(contractor, "contractor")}
                                      variant="ghost"
                                      size="sm"
                                    >
                                      <FileText className="h-4 w-4 mr-2" />
                                      Prepare 1099
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="history">
              <Card>
                <CardHeader>
                  <CardTitle>Filing History</CardTitle>
                  <CardDescription>
                    View and manage your tax filing history
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <FileText className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium">No filing history yet</h3>
                    <p className="text-sm text-muted-foreground max-w-md mt-2">
                      When you prepare and submit 1099 forms, they will appear here for tracking and reference.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="settings">
              <Card>
                <CardHeader>
                  <CardTitle>Filing Settings</CardTitle>
                  <CardDescription>
                    Configure tax filing preferences and settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <FileType className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium">Filing settings coming soon</h3>
                    <p className="text-sm text-muted-foreground max-w-md mt-2">
                      Settings for tax filings, including form preferences and e-filing options, will be available soon.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* 1099 Form Preparation Dialog */}
        <Dialog open={showPrepareDialog} onOpenChange={setShowPrepareDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Prepare 1099 Form</DialogTitle>
              <DialogDescription>
                {recipientType === "vendor" 
                  ? `Prepare a 1099 form for vendor: ${selectedVendor?.name || 'Unknown'}`
                  : `Prepare a 1099 form for contractor: ${selectedContractor?.name || 'Unknown'}`
                }
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="form-type" className="text-right">
                  Form Type
                </Label>
                <Select 
                  value={form1099Type} 
                  onValueChange={setForm1099Type}
                >
                  <SelectTrigger className="col-span-3" id="form-type">
                    <SelectValue placeholder="Select form type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MISC">1099-MISC</SelectItem>
                    <SelectItem value="NEC">1099-NEC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Show different fields based on form type */}
              {form1099Type === "NEC" && (
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="nonemployee-compensation" className="text-right">
                    Nonemployee Compensation (Box 1)
                  </Label>
                  <Input
                    id="nonemployee-compensation"
                    className="col-span-3"
                    placeholder="0.00"
                    value={boxAmounts.nonemployeeCompensation}
                    onChange={(e) => setBoxAmounts({
                      ...boxAmounts,
                      nonemployeeCompensation: e.target.value
                    })}
                  />
                </div>
              )}
              
              {form1099Type === "MISC" && (
                <>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="rents" className="text-right">
                      Rents (Box 1)
                    </Label>
                    <Input
                      id="rents"
                      className="col-span-3"
                      placeholder="0.00"
                      value={boxAmounts.rents}
                      onChange={(e) => setBoxAmounts({
                        ...boxAmounts,
                        rents: e.target.value
                      })}
                    />
                  </div>
                  
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="royalties" className="text-right">
                      Royalties (Box 2)
                    </Label>
                    <Input
                      id="royalties"
                      className="col-span-3"
                      placeholder="0.00"
                      value={boxAmounts.royalties}
                      onChange={(e) => setBoxAmounts({
                        ...boxAmounts,
                        royalties: e.target.value
                      })}
                    />
                  </div>
                  
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="other-income" className="text-right">
                      Other Income (Box 3)
                    </Label>
                    <Input
                      id="other-income"
                      className="col-span-3"
                      placeholder="0.00"
                      value={boxAmounts.otherIncome}
                      onChange={(e) => setBoxAmounts({
                        ...boxAmounts,
                        otherIncome: e.target.value
                      })}
                    />
                  </div>
                </>
              )}
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="federal-tax-withheld" className="text-right">
                  Federal Tax Withheld
                </Label>
                <Input
                  id="federal-tax-withheld"
                  className="col-span-3"
                  placeholder="0.00"
                  value={boxAmounts.federalTaxWithheld}
                  onChange={(e) => setBoxAmounts({
                    ...boxAmounts,
                    federalTaxWithheld: e.target.value
                  })}
                />
              </div>
              
              {/* Show PDF preview if generated */}
              {generatedForm && (
                <div className="mt-4">
                  <h3 className="font-medium mb-2">Form Preview</h3>
                  <div className="border rounded-md p-4 bg-gray-50">
                    <div className="bg-white p-6 shadow-md rounded-md mb-4">
                      <div className="text-center mb-6">
                        <h2 className="text-xl font-bold text-gray-800">1099 Form Preview</h2>
                        <p className="text-sm text-gray-500">Generated: {new Date().toLocaleDateString()}</p>
                      </div>
                      
                      <div className="border-b pb-4 mb-4">
                        <h3 className="font-medium text-gray-800">Form 1099-{form1099Type}</h3>
                        <p className="text-sm text-gray-600">Tax Year: {selectedYear}</p>
                      </div>
                      
                      <div className="bg-blue-50 p-4 rounded-md mb-4">
                        <h3 className="font-medium text-blue-700 mb-1">PAYER (Your Business)</h3>
                        <p className="text-sm">{businessInfo?.name || "Business Name"}</p>
                        {businessInfo?.address && (
                          <p className="text-sm text-gray-600">
                            {businessInfo.address.street}<br />
                            {businessInfo.address.city}, {businessInfo.address.state} {businessInfo.address.zipCode}
                          </p>
                        )}
                      </div>
                      
                      <div className="bg-gray-50 p-4 rounded-md mb-4">
                        <h3 className="font-medium text-gray-700 mb-1">
                          RECIPIENT ({recipientType === "vendor" ? "Vendor" : "Contractor"})
                        </h3>
                        <p className="text-sm">
                          {recipientType === "vendor" 
                            ? selectedVendor?.name 
                            : selectedContractor?.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          Tax ID: {recipientType === "vendor" 
                            ? (selectedVendor?.taxId || "Not provided") 
                            : (selectedContractor?.taxId || "Not provided")}
                        </p>
                      </div>
                      
                      <div className="mb-4">
                        <h3 className="font-medium text-gray-700 mb-2">PAYMENT INFORMATION</h3>
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-blue-600 text-white">
                              <th className="p-2 text-left">Description</th>
                              <th className="p-2 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {form1099Type === "MISC" ? (
                              <>
                                {boxAmounts.rents && (
                                  <tr className="border-b">
                                    <td className="p-2">Box 1: Rents</td>
                                    <td className="p-2 text-right">${boxAmounts.rents}</td>
                                  </tr>
                                )}
                                {boxAmounts.royalties && (
                                  <tr className="border-b">
                                    <td className="p-2">Box 2: Royalties</td>
                                    <td className="p-2 text-right">${boxAmounts.royalties}</td>
                                  </tr>
                                )}
                                {boxAmounts.otherIncome && (
                                  <tr className="border-b">
                                    <td className="p-2">Box 3: Other Income</td>
                                    <td className="p-2 text-right">${boxAmounts.otherIncome}</td>
                                  </tr>
                                )}
                              </>
                            ) : (
                              <>
                                {boxAmounts.nonemployeeCompensation && (
                                  <tr className="border-b">
                                    <td className="p-2">Box 1: Nonemployee Compensation</td>
                                    <td className="p-2 text-right">${boxAmounts.nonemployeeCompensation}</td>
                                  </tr>
                                )}
                              </>
                            )}
                            {boxAmounts.federalTaxWithheld && (
                              <tr className="border-b">
                                <td className="p-2">Federal Income Tax Withheld</td>
                                <td className="p-2 text-right">${boxAmounts.federalTaxWithheld}</td>
                              </tr>
                            )}
                            {!Object.values(boxAmounts).some(val => val) && (
                              <tr>
                                <td className="p-2 text-gray-500">No payment information entered</td>
                                <td className="p-2 text-right">-</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="mt-8 border-t pt-4">
                        <div className="border-b border-gray-400 w-48 mb-1"></div>
                        <p className="text-xs text-gray-500">Payer Signature</p>
                      </div>
                      
                      <div className="mt-8 pt-4 text-center">
                        <p className="text-xs text-gray-400">
                          DISCLAIMER: This is a preview and not an official IRS form. 
                          Please consult with a tax professional before filing.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleDownloadForm}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPrepareDialog(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleFormSubmit} disabled={generatedForm !== null}>
                <FileText className="h-4 w-4 mr-2" />
                {generatedForm ? "Form Prepared" : "Prepare Form"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}